import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Badge,
  Button,
  Divider,
  EmptyState,
  List,
  Panel,
  Progress,
  SegmentedControl,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercentage,
  type BadgeTone,
  type ListItemData,
} from 'escrowbuild-ui';

import {
  useDocuments,
  useEvidence,
  useMilestones,
  usePublishedPublications,
  useScheduleOf,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import {
  findInvestor,
  findProject,
  participationsOf,
  projectTotals,
  type DocumentRecord,
  type Participation,
  type SchedulePhase,
} from '@/data/domain';

/*
  The schedule stores a phase's position and width on a 12-month axis of 0–100,
  so a month is a twelfth of it. The figure stays labelled as a position on that
  axis: the axis carries no origin date, and deriving a calendar date from it
  would put a date on screen that no record holds.
*/
const AXIS_MONTH = 100 / 12;

const TH =
  'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';

const PHASE_TONE: Record<SchedulePhase['status'], BadgeTone> = {
  completed: 'success',
  'in-progress': 'info',
  planned: 'neutral',
};

const PHASE_LABEL: Record<SchedulePhase['status'], AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

const PARTICIPATION_TONE: Record<Participation['status'], BadgeTone> = {
  confirmed: 'success',
  pending: 'warning',
  draft: 'neutral',
};

const PARTICIPATION_LABEL: Record<Participation['status'], AppTranslationKey> = {
  confirmed: 'status.participation.confirmed',
  pending: 'status.participation.pending',
  draft: 'status.participation.draft',
};

const DOCUMENT_TONE: Record<DocumentRecord['status'], BadgeTone> = {
  verified: 'success',
  'in-review': 'info',
  rejected: 'error',
};

/**
 * The document's KIND as a label.
 *
 * Separate from `DOCUMENT_LABEL` above, which labels the verification state: a
 * row shows both, and one map holding both would have to be keyed by two
 * different unions. This one exists because the cell below printed the wire code
 * itself — `invoice`, `permit` — which `capitalize` turned into a word that only
 * reads as English.
 */
const DOCUMENT_KIND_LABEL: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

const DOCUMENT_LABEL: Record<DocumentRecord['status'], AppTranslationKey> = {
  verified: 'status.verification.verified',
  'in-review': 'status.verification.inReview',
  rejected: 'status.verification.rejected',
};

/**
 * The review-state facet's options. They hold keys rather than labels because a
 * list built at module scope is built before any provider exists — the component
 * that renders them resolves them, so they follow the language selector.
 */
const DOCUMENT_FILTERS: { value: string; labelKey: AppTranslationKey }[] = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'verified', labelKey: 'status.verification.verified' },
  { value: 'in-review', labelKey: 'status.verification.inReview' },
  { value: 'rejected', labelKey: 'status.verification.rejected' },
];

/** An update and a milestone share one shape once they are read as a feed. */
interface ActivityEntry {
  id: string;
  title: string;
  kind: string;
  context: string;
  date: string;
}

/**
 * The default tab of a development: where it is, what is coming, what has just
 * happened, who paid for it and what has been filed.
 *
 * Every figure here is a slice of a set the shell also reads whole — the phase
 * rows sum to the spend metric above them, and the participation rows sum to the
 * committed-capital metric — so a client comparing this page with the header
 * finds the same arithmetic rather than a second opinion.
 */
export function ProjectOverviewPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [documentFilter, setDocumentFilter] = useState('all');

  // The span's month length is the one part of it that is interface copy.
  // `count` selects the plural; `months` is what the reader sees, formatted for
  // the locale. The length is fractional — 26 units of the axis is 3.1 months —
  // so interpolating the raw count printed `3.1199999999999997`.
  const monthsLabel = (months: number) =>
    t('project.overview.months', {
      count: months,
      months: formatNumber(months, { precision: 1 }),
    });

  const project = findProject(projectId);

  /*
     The hooks run before the not-found return below, because a hook cannot run
     after a conditional one. The empty development that branch passes selects
     nothing at all, and the branch renders an error state rather than any of
     these lists.
  */
  const id = project?.id ?? '';
  const phases = useScheduleOf(id);
  const milestones = useMilestones(id);
  const releases = usePublishedPublications(id);
  const evidence = useEvidence(id);
  const documents = useDocuments(id);

  if (!project) {
    return (
      <Panel padding="lg" title={t('project.shell.notFoundTitle')}>
        <EmptyState
          variant="page"
          title={t('project.shell.notFoundHeading')}
          description={t('project.overview.notFoundMessage', { id: projectId ?? '' })}
        />
      </Panel>
    );
  }

  const totals = projectTotals(project.id);
  const phaseName = (phaseId: string) =>
    phases.find((phase) => phase.id === phaseId)?.name ?? phaseId;

  // A development with nothing running still has a phase to talk about, which is
  // the next one planned — an empty panel would read as missing data instead.
  const currentPhase =
    phases.find((phase) => phase.status === 'in-progress') ??
    phases.find((phase) => phase.status === 'planned') ??
    null;

  const phaseEvidence = currentPhase
    ? evidence.filter((item) => item.phaseId === currentPhase.id)
    : [];
  const phaseVariance = currentPhase ? currentPhase.actual - currentPhase.budgeted : 0;
  const spendShare =
    currentPhase && currentPhase.budgeted > 0
      ? Math.min(100, Math.round((currentPhase.actual / currentPhase.budgeted) * 100))
      : 0;

  const upcomingItems: ListItemData[] = milestones
    .filter((milestone) => milestone.status === 'upcoming')
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((milestone) => ({
      id: milestone.id,
      title: milestone.title,
      subtitle: `${phaseName(milestone.phaseId)} · ${formatDate(milestone.date, { style: 'medium' })}`,
      // The flag is the whole point of the column: a milestone the investors were
      // told about reads differently from one still internal, and silence is the
      // honest rendering of the internal case.
      trailing: milestone.notified ? (
        <Badge tone="info" variant="subtle" size="sm">
          {t('project.overview.investorNotified')}
        </Badge>
      ) : undefined,
    }));

  const activity: ActivityEntry[] = [
    ...releases.map((publication) => ({
      id: publication.id,
      title: publication.title,
      kind: t('project.overview.activity.published'),
      context: project.name,
      date: publication.publishedAt ?? '',
    })),
    ...milestones
      .filter((milestone) => milestone.status === 'reached')
      .map((milestone) => ({
        id: milestone.id,
        title: milestone.title,
        kind: t('project.overview.activity.milestone'),
        context: phaseName(milestone.phaseId),
        date: milestone.date,
      })),
  ]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);

  const activityItems: ListItemData[] = activity.map((entry) => ({
    id: entry.id,
    title: entry.title,
    subtitle: `${entry.kind} · ${entry.context}`,
    trailing: formatDate(entry.date, { style: 'medium' }),
  }));

  const participations = participationsOf(project.id);
  const investorShare = participations.reduce((sum, record) => sum + record.sharePercent, 0);
  const investorCommitted = participations.reduce((sum, record) => sum + record.committed, 0);
  const investorContributed = participations.reduce((sum, record) => sum + record.contributed, 0);

  /* A copy: the hook memoises its array per locale, and sorting it in place would
     re-order what every other reader of that memo sees. */
  const filedDocuments = [...documents].sort((a, b) => b.date.localeCompare(a.date));
  const matchingDocuments =
    documentFilter === 'all'
      ? filedDocuments
      : filedDocuments.filter((document) => document.status === documentFilter);
  const visibleDocuments = matchingDocuments.slice(0, 5);
  const documentFilters = DOCUMENT_FILTERS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
  const activeFilterLabel =
    documentFilters.find((option) => option.value === documentFilter)?.label ?? '';

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Panel
        headingLevel={3}
        title={t('project.overview.currentPhase.title')}
        description={t('project.overview.currentPhase.description')}
      >
        {currentPhase ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-display text-lg font-semibold tracking-label text-heading uppercase">
                {currentPhase.name}
              </p>
              <Badge tone={PHASE_TONE[currentPhase.status]} variant="subtle" size="sm">
                {t(PHASE_LABEL[currentPhase.status])}
              </Badge>
            </div>

            <dl className="flex flex-col">
              <DetailRow
                label={t('project.overview.plannedSpan')}
                value={spanLabel(currentPhase.plannedStart, currentPhase.plannedWidth, monthsLabel)}
              />
              <DetailRow
                label={t('project.overview.actualSpan')}
                value={
                  currentPhase.actualStart === null || currentPhase.actualWidth === null
                    ? t('project.overview.notStarted')
                    : spanLabel(currentPhase.actualStart, currentPhase.actualWidth, monthsLabel)
                }
              />
              <DetailRow
                label={t('project.overview.budgeted')}
                value={formatMoney(currentPhase.budgeted, { precision: 0 })}
              />
              <DetailRow
                label={t('project.shell.spent')}
                value={formatMoney(currentPhase.actual, { precision: 0 })}
              />
              <DetailRow
                label={t('project.overview.variance')}
                value={formatMoney(phaseVariance, { precision: 0 })}
                tone={phaseVariance <= 0 ? 'success' : 'danger'}
              />
              <DetailRow
                label={t('project.overview.evidenceFiled')}
                value={t('project.overview.evidenceCount', { count: phaseEvidence.length })}
                hint={t('project.overview.evidenceCleared', {
                  count: phaseEvidence.filter((item) => item.published).length,
                })}
              />
            </dl>

            <Divider variant="subtle" />

            {currentPhase.budgeted > 0 ? (
              <Progress
                value={spendShare}
                tone={phaseVariance <= 0 ? 'success' : 'danger'}
                label={t('project.overview.spendProgress', { phase: currentPhase.name })}
                showValue
              />
            ) : (
              <p className="text-xs text-muted">
                {t('project.overview.noBudget')}
              </p>
            )}
          </div>
        ) : (
          <EmptyState
            variant="cards"
            title={t('project.overview.noPhase.title')}
            description={t('project.overview.noPhase.description')}
          />
        )}
      </Panel>

      <Panel
        headingLevel={3}
        title={t('project.overview.milestones.title')}
        description={t('project.overview.milestones.description')}
        footer={
          <button
            type="button"
            onClick={() => navigate(`/projects/${project.id}/schedule`)}
            className={cn(
              'dhd-target relative text-xs font-medium text-heading underline decoration-line-strong underline-offset-2',
              'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
              PRESS,
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            {t('project.overview.phasesLink', { count: phases.length })}
          </button>
        }
      >
        <List
          label={t('project.overview.milestones.list')}
          size="lg"
          divided
          items={upcomingItems}
          emptyLabel={t('project.overview.milestones.empty')}
        />
      </Panel>

      <Panel
        headingLevel={3}
        title={t('project.overview.activity.title')}
        description={t('project.overview.activity.description')}
      >
        <List
          label={t('project.overview.activity.title')}
          size="lg"
          divided
          items={activityItems}
          emptyLabel={t('project.overview.activity.empty')}
        />
      </Panel>

      <Panel
        headingLevel={3}
        title={t('project.overview.participation.title')}
        description={t('project.overview.participation.description', {
          count: participations.length,
          project: project.name,
        })}
      >
        {participations.length === 0 ? (
          <EmptyState
            variant="table"
            title={t('project.overview.participation.emptyTitle')}
            description={t('project.overview.participation.emptyDescription')}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="dhd-sr-only">
                  {t('project.overview.participation.caption')}
                </caption>
                <thead>
                  <tr className="border-b-2 border-line-strong">
                    <th scope="col" className={cn(TH, 'text-left')}>
                      {t('project.overview.column.investor')}
                    </th>
                    <th scope="col" className={cn(TH, 'text-right')}>
                      {t('project.overview.column.share')}
                    </th>
                    <th scope="col" className={cn(TH, 'text-right')}>
                      {t('project.overview.column.committed')}
                    </th>
                    <th scope="col" className={cn(TH, 'text-right')}>
                      {t('project.shell.contributed')}
                    </th>
                    <th scope="col" className={cn(TH, 'text-left')}>
                      {t('common.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {participations.map((record) => (
                    <tr key={record.id} className="border-b border-line-subtle">
                      <th
                        scope="row"
                        className={cn(TD, 'text-left font-medium text-heading')}
                      >
                        {findInvestor(record.investorId)?.name ?? record.investorId}
                      </th>
                      <td className={cn(TD, 'text-right tabular-nums whitespace-nowrap')}>
                        {formatPercentage(record.sharePercent, { precision: 0 })}
                      </td>
                      <td className={cn(TD, 'text-right tabular-nums whitespace-nowrap')}>
                        {formatMoney(record.committed, { precision: 0 })}
                      </td>
                      <td className={cn(TD, 'text-right tabular-nums whitespace-nowrap')}>
                        {formatMoney(record.contributed, { precision: 0 })}
                      </td>
                      <td className={TD}>
                        <Badge tone={PARTICIPATION_TONE[record.status]} variant="subtle" size="sm">
                          {t(PARTICIPATION_LABEL[record.status])}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-line-strong bg-surface-alt">
                    <th
                      scope="row"
                      className={cn(
                        TD,
                        'text-left font-display text-2xs font-semibold tracking-label text-muted uppercase',
                      )}
                    >
                      {t('common.total')}
                    </th>
                    <td className={cn(TD, 'text-right font-medium tabular-nums whitespace-nowrap text-heading')}>
                      {formatPercentage(investorShare, { precision: 0 })}
                    </td>
                    <td className={cn(TD, 'text-right font-medium tabular-nums whitespace-nowrap text-heading')}>
                      {formatMoney(investorCommitted, { precision: 0 })}
                    </td>
                    <td className={cn(TD, 'text-right font-medium tabular-nums whitespace-nowrap text-heading')}>
                      {formatMoney(investorContributed, { precision: 0 })}
                    </td>
                    <td className={TD} />
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="mt-3 text-xs text-muted">
              {t('project.overview.participation.summary', {
                investorShare: formatPercentage(investorShare, { precision: 0 }),
                ownership: formatPercentage(project.ownershipShare, { precision: 0 }),
                total: formatPercentage(investorShare + project.ownershipShare, { precision: 0 }),
                contributed: formatMoney(investorContributed, { precision: 0 }),
                committed: formatMoney(totals.contributed, { precision: 0 }),
              })}
            </p>
          </>
        )}
      </Panel>

      <Panel
        headingLevel={3}
        title={t('project.overview.documents.title')}
        description={t('project.overview.documents.description', { count: filedDocuments.length })}
        className="xl:col-span-2"
      >
        <div className="flex flex-col gap-4">
          <SegmentedControl
            label={t('project.overview.documents.filterLabel')}
            size="sm"
            options={documentFilters}
            value={documentFilter}
            onValueChange={setDocumentFilter}
          />

          {visibleDocuments.length === 0 ? (
            <EmptyState
              variant="table"
              title={t('project.overview.documents.emptyTitle', {
                state: activeFilterLabel.toLowerCase(),
              })}
              description={t('project.overview.documents.emptyDescription')}
              action={
                <Button size="sm" variant="outline" onClick={() => setDocumentFilter('all')}>
                  {t('project.overview.documents.showAll')}
                </Button>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="dhd-sr-only">
                    {t('project.overview.documents.caption')}
                  </caption>
                  <thead>
                    <tr className="border-b-2 border-line-strong">
                      <th scope="col" className={cn(TH, 'text-left')}>
                        {t('project.overview.column.document')}
                      </th>
                      <th scope="col" className={cn(TH, 'text-left')}>
                        {t('project.overview.column.type')}
                      </th>
                      <th scope="col" className={cn(TH, 'text-left')}>
                        {t('common.date')}
                      </th>
                      <th scope="col" className={cn(TH, 'text-left')}>
                        {t('project.overview.column.review')}
                      </th>
                      <th scope="col" className={cn(TH, 'text-left')}>
                        {t('project.overview.column.publication')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDocuments.map((document) => (
                      <tr key={document.id} className="border-b border-line-subtle">
                        <th
                          scope="row"
                          className={cn(TD, 'text-left font-medium text-heading')}
                        >
                          {document.name}
                        </th>
                        <td className={TD}>{t(DOCUMENT_KIND_LABEL[document.type])}</td>
                        <td className={cn(TD, 'whitespace-nowrap tabular-nums')}>
                          {formatDate(document.date, { style: 'medium' })}
                        </td>
                        <td className={TD}>
                          <Badge
                            tone={DOCUMENT_TONE[document.status]}
                            variant="subtle"
                            size="sm"
                          >
                            {t(DOCUMENT_LABEL[document.status])}
                          </Badge>
                        </td>
                        <td className={TD}>
                          <Badge
                            tone={document.published ? 'success' : 'neutral'}
                            variant="subtle"
                            size="sm"
                          >
                            {t(
                              document.published
                                ? 'project.overview.investorVisible'
                                : 'project.overview.internalOnly',
                            )}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {matchingDocuments.length > visibleDocuments.length ? (
                <p className="text-2xs text-muted">
                  {t('project.overview.documents.showing', {
                    count: matchingDocuments.length,
                    shown: visibleDocuments.length,
                  })}
                </p>
              ) : null}
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}

/** One label/value line of a panel's detail list. */
function DetailRow({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'success' | 'danger';
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line-subtle py-2 last:border-b-0">
      <dt className="text-2xs font-semibold tracking-label text-muted uppercase">{label}</dt>
      <dd className="text-right">
        <span
          className={cn(
            'text-sm tabular-nums',
            tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-heading',
          )}
        >
          {value}
        </span>
        {hint ? <span className="mt-0.5 block text-2xs text-muted">{hint}</span> : null}
      </dd>
    </div>
  );
}

/**
 * A phase span as month positions on the axis: when it runs, and for how long.
 * The length arrives already worded — this is not a component, so it has no `t`,
 * and the count it needs is its own `width` argument.
 */
function spanLabel(start: number, width: number, monthsLabel: (months: number) => string): string {
  const from = start / AXIS_MONTH;
  const to = (start + width) / AXIS_MONTH;
  return `${formatNumber(from, { precision: 1 })} – ${formatNumber(to, { precision: 1 })} (${monthsLabel(
    width / AXIS_MONTH,
  )})`;
}
